import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { IndicatorSelectHandler } from '@/entities/dashboards';
import { EMPTY_VALUE, cn, preventShiftSelection, type QualityZoneColor, zoneBarClass } from '@/shared/lib';
import { EmptyState, TruncatedText } from '@/shared/ui';

/** Один элемент горизонтальной столбчатой диаграммы */
export type BarChartItem = {
  label: string;
  /** Показанная величина; `null` — величины нет, вместо неё стоит прочерк */
  value: number | null;
  /** Необязательный суффикс у значения (например, «%») */
  suffix?: string;
  /** Разговоры показателя — по ним строка открывает список диалогов; пусто — строка не кликабельна */
  conversationIds?: number[];
  /**
   * Максимум шкалы показателя. Если задан — ширина бара нормируется его долей `value/maxScore`,
   * а рядом со значением показывается сам максимум («avg / max»). Без него ширина нормируется по
   * общему `maxValue` диаграммы, а если и его нет — бар остаётся минимальной ширины.
   */
  maxScore?: number | null;
  /**
   * Готовая доля заполнения бара (0..1) — приоритетнее `maxScore` и общего `maxValue`.
   *
   * Нужна показателю, собранному из оценок с разными шкалами: их баллы несопоставимы, и долю
   * заполнения считает бэкенд по каждой оценке отдельно, а не клиент делением среднего балла
   * на чужой максимум.
   */
  share?: number | null;
  /** Зона окраски бара по единой шкале зон; при отсутствии бар красится дефолтно (`negative`/`primary`). */
  zone?: QualityZoneColor;
};

/** Формат отображения числовых значений на диаграмме */
export type BarChartValueFormat = 'integer' | 'decimal-1' | 'currency-rub';

/** Ширина колонки лейбла слева от полосы */
export type BarChartLabelWidth = 'narrow' | 'wide';

/** Форматирует максимум шкалы: целое — без дробной части, иначе с одним знаком. */
const formatMax = (max: number): string => (Number.isInteger(max) ? String(max) : max.toFixed(1));

/** Форматирование значения согласно выбранному формату */
const formatValue = (value: number, format: BarChartValueFormat): string => {
  if (format === 'integer') {
    return Math.round(value).toLocaleString('ru-RU');
  }
  if (format === 'currency-rub') {
    return Math.round(value).toLocaleString('ru-RU');
  }
  return value.toFixed(1);
};

type Props = {
  items: BarChartItem[];
  /**
   * Максимум шкалы, к которому нормируется ширина баров. Не нужен диаграмме, где каждый бар
   * нормируется своей долей (`share`) или своим максимумом (`maxScore`), — там его не задают.
   */
  maxValue?: number;
  /** true — красные бары (для «плохих» метрик: процент низких оценок и т.п.) */
  negative?: boolean;
  /** Формат значений. По умолчанию `decimal-1` (`123.4`) */
  format?: BarChartValueFormat;
  /** Ширина колонки лейбла. `wide` — для длинных названий с переносом */
  labelWidth?: BarChartLabelWidth;
  /** Переход к диалогам строки; не задан — строки остаются некликабельными */
  onSelect?: IndicatorSelectHandler;
};

/**
 * Горизонтальная столбчатая диаграмма: подпись — бар — значение.
 * Колонка подписи настраивается через `labelWidth`, формат величины — через `format`.
 *
 * Место под величину отмеряется по самой длинной величине ряда (`max-content` последней колонки),
 * а не постоянной шириной: балл с крупным потолком («100.0 / 100») остаётся одной строкой, строки
 * держат одну высоту, а полосы всех строк начинаются и заканчиваются на одной вертикали. Место
 * забирается у подписи — не поместившееся название усекается и целиком раскрывается подсказкой.
 *
 * Кликабельность выводится из данных: есть разговоры и обработчик — строка ведёт к диалогам,
 * нет — обычная строка графика. Элемент опознаётся позицией, а не подписью: подписи в пределах
 * одной карточки могут совпасть (однофамильцы в рейтинге), и клик увёл бы к чужой выборке.
 */
export const BarChart: FC<Props> = ({
  items,
  maxValue,
  negative,
  format = 'decimal-1',
  labelWidth = 'narrow',
  onSelect,
}) => {
  const { t } = useTranslation('common');

  if (items.length === 0) return <EmptyState />;

  const isWide = labelWidth === 'wide';

  // Колонки задаёт сетка всей диаграммы: место под величину отмеряется по самой длинной величине
  // ряда сразу (`max-content`), а не по каждой строке отдельно. Ширину колонки подписи выбирает
  // `labelWidth` — длинным названиям её отводится больше.
  const chartClassName = isWide
    ? 'grid grid-cols-[minmax(160px,280px)_1fr_max-content] gap-y-2'
    : 'grid grid-cols-[minmax(60px,180px)_1fr_max-content] gap-y-2';

  // Строка — подсетка этой сетки: колонки у всех строк общие, а строка при этом остаётся
  // собственным блоком — ей достаются наведение и нажатие.
  const rowClassName = 'grid grid-cols-subgrid col-span-3 gap-2 items-center';

  return (
    <div className={chartClassName}>
      {items.map((item, index) => {
        // Готовая доля заполнения — первична; иначе нормируем по своему максимуму шкалы,
        // а при его отсутствии — по общему максимуму диаграммы.
        const hasOwnMax = typeof item.maxScore === 'number' && item.maxScore > 0;
        const denom = hasOwnMax ? (item.maxScore as number) : (maxValue ?? 0);
        const ownPct = denom > 0 && item.value !== null ? (item.value / denom) * 100 : 0;
        const pct = typeof item.share === 'number' ? item.share * 100 : ownPct;
        const width = `${Math.max(2, Math.min(100, pct))}%`;
        const valueSuffix = hasOwnMax ? ` / ${formatMax(item.maxScore as number)}` : (item.suffix ?? '');
        const valueText = item.value === null ? EMPTY_VALUE : `${formatValue(item.value, format)}${valueSuffix}`;
        const fillClass = item.zone ? zoneBarClass[item.zone] : negative ? 'bg-destructive/60' : 'bg-primary';
        const isClickable = !!onSelect && !!item.conversationIds?.length;
        const rowContent = (
          <>
            {/* Место под величину забирается у названия — целиком оно доступно по наведению */}
            <TruncatedText className="text-sm" lines={isWide ? 2 : 1}>
              {item.label}
            </TruncatedText>
            <div className="h-4 bg-muted rounded-full overflow-hidden">
              <div className={cn('h-full rounded-full transition-all', fillClass)} style={{ width }} />
            </div>
            <span className="text-sm font-bold text-right whitespace-nowrap">{valueText}</span>
          </>
        );

        if (!isClickable) {
          return (
            <div key={index} className={rowClassName}>
              {rowContent}
            </div>
          );
        }

        return (
          <div
            key={index}
            role="button"
            tabIndex={-1}
            aria-label={t('barChart.openDialogs')}
            className={cn(rowClassName, 'cursor-pointer rounded-md hover:bg-muted/50')}
            onMouseDown={preventShiftSelection}
            onClick={(event) => onSelect?.(item, event.shiftKey)}
          >
            {rowContent}
          </div>
        );
      })}
    </div>
  );
};
